package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import org.w3c.dom.events.Event
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.Element
import se.mittpass.shared.api.PayrollRecordDto

internal enum class BarHandle { LEFT, RIGHT, BODY }

internal data class BarDragState(
    val recordId: Long,
    val handle: BarHandle,
    val origStartMin: Int,
    val origEndMin: Int,
    val containerLeft: Double,
    val containerWidth: Double,
    val windowStartMin: Int,
    val windowDurMin: Int,
    val currentStartMin: Int = origStartMin,
    val currentEndMin: Int = origEndMin
)

internal fun timeToMin(t: String): Int {
    val parts = t.split(":")
    return parts[0].toInt() * 60 + (parts.getOrNull(1)?.toInt() ?: 0)
}

internal fun minToTimeStr(min: Int): String {
    val h = (min / 60).coerceIn(0, 23)
    val m = min % 60
    return "${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00"
}

internal fun fmtMin(min: Int) =
    "${(min / 60).toString().padStart(2, '0')}:${(min % 60).toString().padStart(2, '0')}"

// ── Three-column table row: Schema | Incheckning | Lönegrundande ──
@Composable
internal fun PayrollTimelineBar(
    rec: PayrollRecordDto,
    drag: BarDragState?,
    onDragStart: (BarDragState) -> Unit,
    onDragUpdate: (BarDragState) -> Unit,
    onDragEnd: (BarDragState) -> Unit,
    windowStartHour: Int = 6,
    windowEndHour: Int = 22,
    isMobile: Boolean = false
) {
    val paidStart = fmtMin(timeToMin(rec.startTime))
    val paidEnd   = fmtMin(timeToMin(rec.endTime))
    val netMin    = rec.netMinutes
    val netLabel  = "${netMin / 60}h${if (netMin % 60 > 0) " ${netMin % 60}m" else ""}"

    val ciStr = rec.checkInTime?.take(5)
    val coStr = rec.checkOutTime?.take(5)

    val schedStart = rec.slotStartTime?.take(5)
    val schedEnd   = rec.slotEndTime?.take(5)

    // Diff between paid net and attendance
    val attendMin = if (ciStr != null && coStr != null)
        (timeToMin(rec.checkOutTime!!) - timeToMin(rec.checkInTime!!)).coerceAtLeast(0) else null
    val diffMin = if (attendMin != null) netMin - attendMin else null
    val hasDiff = diffMin != null && diffMin != 0

    Div({ style {
        property("display", "grid")
        property("grid-template-columns", if (isMobile) "repeat(auto-fit, minmax(90px, 1fr))" else "repeat(3, minmax(110px, 1fr))")
        property("gap", if (isMobile) "0.35rem" else "0.5rem")
        property("width", "100%")
    } }) {

        // Column 1: Schema
        TimeCol(
            label = "Schema",
            value = if (schedStart != null && schedEnd != null) "$schedStart – $schedEnd" else "–",
            sub = null,
            dimmed = schedStart == null,
            isMobile = isMobile
        )

        // Column 2: Incheckning
        TimeCol(
            label = "Incheckning",
            value = if (ciStr != null) "$ciStr – ${coStr ?: "–"}" else "–",
            sub = if (attendMin != null) {
                val h = attendMin / 60; val m = attendMin % 60
                "${h}h${if (m > 0) " ${m}m" else ""}"
            } else null,
            dimmed = ciStr == null,
            highlight = hasDiff,
            isMobile = isMobile
        )

        // Column 3: Lönegrundande
        TimeCol(
            label = "Lönegrundande",
            value = "$paidStart – $paidEnd",
            sub = netLabel + if (rec.breakMinutes > 0) " (${rec.breakMinutes}m rast)" else "",
            dimmed = false,
            accent = true,
            diffMin = diffMin,
            isMobile = isMobile
        )
    }
}

@Composable
private fun TimeCol(
    label: String,
    value: String,
    sub: String?,
    dimmed: Boolean,
    highlight: Boolean = false,
    accent: Boolean = false,
    diffMin: Int? = null,
    isMobile: Boolean = false
) {
    Div({ style {
        property("display", "flex"); property("flex-direction", "column")
        property("padding", "0 0.25rem 0 0")
        property("min-width", if (isMobile) "0" else "105px")
        property("flex-shrink", if (isMobile) "1" else "0")
    } }) {
        // Label
        Span({ style {
            property("font-size", "0.62rem"); property("font-weight", "700")
            property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
            property("color", if (accent) "var(--rp-pine)" else "var(--rp-muted)")
            property("margin-bottom", "0.15rem")
        } }) { Text(label) }

        // Value
        Span({ style {
            property("font-size", "0.88rem"); property("font-weight", "700")
            property("color", when {
                dimmed -> "var(--rp-hl-med)"
                accent -> "var(--rp-text)"
                else -> "var(--rp-text)"
            })
            property("white-space", "nowrap")
        } }) { Text(value) }

        // Sub-label row
        Div({ style {
            property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
            property("margin-top", "0.1rem")
        } }) {
            if (sub != null) {
                Span({ style {
                    property("font-size", "0.72rem")
                    property("color", if (highlight) "var(--rp-warn-text)" else "var(--rp-muted)")
                } }) { Text(sub) }
            }
            // Diff badge on lönegrundande column
            if (diffMin != null && diffMin != 0) {
                val over = diffMin > 0
                val absLabel = kotlin.math.abs(diffMin).let { "${it/60}h${if (it%60>0) " ${it%60}m" else ""}" }
                Span({ style {
                    property("font-size", "0.62rem"); property("font-weight", "700")
                    property("padding", "0.1rem 0.35rem"); property("border-radius", "6px")
                    property("background", if (over) "var(--rp-warn-bg)" else "var(--rp-err-bg)")
                    property("color", if (over) "var(--rp-warn-text)" else "var(--rp-err-text)")
                } }) { Text(if (over) "+$absLabel" else "-$absLabel") }
            }
        }
    }
}

// Legend — shown once above the rows
@Composable
internal fun PayrollBarLegend() {
    Div({ style {
        property("display", "flex"); property("gap", "1.5rem"); property("margin-bottom", "0.25rem")
        property("padding", "0 0.25rem"); property("flex-wrap", "wrap")
    } }) {
        listOf(
            "Schema" to "var(--rp-muted)",
            "Incheckning" to "var(--rp-text)",
            "Lönegrundande" to "var(--rp-pine)"
        ).forEach { (lbl, color) ->
            Span({ style {
                property("font-size", "0.65rem"); property("font-weight", "700")
                property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                property("color", color)
            } }) { Text(lbl) }
        }
    }
}
